import React, { useState, useCallback } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { Box } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';

const useStyles = makeStyles((theme) => ({
  orangeFocused: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      backgroundColor: '#ffffff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 14,
      borderColor: '#dddddd',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-root': {
      color: '#666666',
      fontSize: '0.9rem',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#FF7F27',
    },
  },
  dialogTitle: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
  },
  dialogTitleText: {
    color: '#002D55',
    fontWeight: 600,
    fontSize: '1.15rem',
  },
  dialogContent: {
    padding: '24px !important',
    overflow: 'auto',
    backgroundColor: '#e6eaf0',
  },
  sectionLabel: {
    fontSize: 12,
    color: '#666666',
    marginBottom: 8,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sectionPanel: {
    backgroundColor: '#f7faff',
    borderRadius: 18,
    padding: 16,
    border: '1px solid #dce8f5',
    marginBottom: 24,
  },
  switchBase: {
    color: '#FF7F27',
    '&.Mui-checked': {
      color: '#FF7F27',
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#FF7F27',
    },
  },
  switchTrack: {
    backgroundColor: '#bfbfbf',
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  switchLabel: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: '0.875rem',
  },
  assessmentOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  checkbox: {
    color: '#002D55',
    '&.Mui-checked': {
      color: '#FF7F27',
    },
  },
  checkboxLabel: {
    color: '#002D55',
    fontSize: '0.84rem',
    fontWeight: 600,
  },
  errorText: {
    color: '#c62828',
    fontSize: '0.82rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  noteBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f7fb',
    borderRadius: 14,
    border: '1px solid #dce8f5',
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    padding: '6px 16px',
    textTransform: 'none',
    minWidth: 100,
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  confirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 50,
    border: 'none',
    boxShadow: 'none',
    padding: '6px 16px',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 120,
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  dialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'flex-end',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
  },
}));

// Countries from backend countries.py
const COUNTRIES = [
  'Denmark',
  'Sweden',
  'Norway',
  'Finland',
  'Iceland',
  'Germany',
  'Belgium',
  'Netherlands',
  'France',
  'Wales',
  'Scotland',
  'England',
  'Ireland',
  'Northern Ireland',
  'Luxembourg',
  'Monaco',
  'Spain',
  'Portugal',
  'Switzerland',
  'Liechtenstein',
  'Austria',
  'Italy',
  'Poland',
  'Czech republic',
  'Slovakia',
  'Hungary',
  'Croatia',
  'Bosnia / Herzegovina',
  'Serbia',
  'Montenegro',
  'Macedonia',
  'Bulgaria',
  'Greece',
  'Cyprus',
  'Malta',
  'Romania',
  'Moldova',
  'Ukraine',
  'Belarus',
  'Lithuania',
  'Latvia',
  'Estonia',
  'Russia',
  'Turkey',
  'United States',
  'Other'
];

// Map countries to their social security ID types
const PII_TYPE_MAP = {
  'Denmark': 'CPR',
  'Germany': 'SVNR',
  'Sweden': 'Personnummer',
  'France': 'NIR',
  'England': 'NHS Number',
  'Scotland': 'NHS Number',
  'Wales': 'NHS Number',
  'Northern Ireland': 'NHS Number',
  'United States': 'SSN',
  'Italy': 'Codice Fiscale',
  'Spain': 'NUSS/NIE'
};

const getPiiTypeForCountry = (country) => {
  return PII_TYPE_MAP[country] || 'Other';
};

const CreatePatientDialog = ({ 
  open, 
  token, 
  onClose, 
  onPatientCreated 
}) => {
  const classes = useStyles();
  const [newPatientData, setNewPatientData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    country: '',
    pii_type: 'CPR',
    pii_number: '',
    send_assessment: true,
    send_full_assessment: true,
    send_short_assessment: false
  });
  const [createPatientLoading, setCreatePatientLoading] = useState(false);
  const [createPatientError, setCreatePatientError] = useState('');

  const handleCloseDialog = useCallback(() => {
    setNewPatientData({ email: '', first_name: '', last_name: '', country: '', pii_type: 'CPR', pii_number: '', send_assessment: true, send_full_assessment: true, send_short_assessment: false });
    setCreatePatientError('');
    onClose();
  }, [onClose]);

  const handleCreatePatient = useCallback(async () => {
    // Validate fields
    if (!newPatientData.email || !newPatientData.first_name || !newPatientData.last_name) {
      setCreatePatientError('Email, first name, and last name are required');
      return;
    }
    
    if (!newPatientData.country) {
      setCreatePatientError('Country of residence is required');
      return;
    }

    if (newPatientData.send_assessment && !newPatientData.send_full_assessment && !newPatientData.send_short_assessment) {
      setCreatePatientError('Please choose full or short assessment, or turn off sending');
      return;
    }
    setCreatePatientLoading(true);
    setCreatePatientError('');
    
    try {
      const response = await fetch('https://api.virtus-one.com/user/create_patient/', {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newPatientData.email.toLowerCase(),
          first_name: newPatientData.first_name,
          last_name: newPatientData.last_name,
          pii_type: newPatientData.pii_type,
          pii_number: newPatientData.pii_number,
            send_full_assessment: newPatientData.send_assessment ? !!newPatientData.send_full_assessment : false,
            send_short_assessment: newPatientData.send_assessment ? !!newPatientData.send_short_assessment : false
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        handleCloseDialog();
        // Call the parent's callback with the new patient data
        if (onPatientCreated) {
          onPatientCreated(data);
        }
      } else {
        const error = await response.json();
        setCreatePatientError(error.error || 'Failed to create patient');
      }
    } catch (err) {
      console.error('Create patient error:', err);
      setCreatePatientError('Network error: Failed to create patient');
    } finally {
      setCreatePatientLoading(false);
    }
  }, [newPatientData, token, onPatientCreated, handleCloseDialog]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      keepMounted
      PaperProps={{ style: { borderRadius: 18 } }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.dialogTitleText}>
          Create New Patient
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {createPatientError && (
          <Typography className={classes.errorText}>
            {createPatientError}
          </Typography>
        )}

        <Typography className={classes.sectionLabel}>Patient Details</Typography>
        <Box className={classes.sectionPanel}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={newPatientData.email}
              onChange={(e) => setNewPatientData(prev => ({ ...prev, email: e.target.value }))}
              variant="outlined"
              className={classes.orangeFocused}
              disabled={createPatientLoading}
            />
            <TextField
              label="First Name"
              required
              fullWidth
              value={newPatientData.first_name}
              onChange={(e) => setNewPatientData(prev => ({ ...prev, first_name: e.target.value }))}
              variant="outlined"
              className={classes.orangeFocused}
              disabled={createPatientLoading}
            />
            <TextField
              label="Last Name"
              required
              fullWidth
              value={newPatientData.last_name}
              onChange={(e) => setNewPatientData(prev => ({ ...prev, last_name: e.target.value }))}
              variant="outlined"
              className={classes.orangeFocused}
              disabled={createPatientLoading}
            />
            <TextField
              label="Country of Residence"
              select
              fullWidth
              value={newPatientData.country}
              onChange={(e) => {
                const selectedCountry = e.target.value;
                setNewPatientData(prev => ({ 
                  ...prev, 
                  country: selectedCountry,
                  pii_type: getPiiTypeForCountry(selectedCountry)
                }));
              }}
              variant="outlined"
              className={classes.orangeFocused}
              disabled={createPatientLoading}
              SelectProps={{
                native: true,
              }}
            >
              <option value=""></option>
              {COUNTRIES.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </TextField>
            <TextField
              label="Social Security Number (Type)"
              select
              fullWidth
              value={newPatientData.pii_type}
              onChange={(e) => setNewPatientData(prev => ({ ...prev, pii_type: e.target.value }))}
              variant="outlined"
              className={classes.orangeFocused}
              disabled={createPatientLoading}
              SelectProps={{
                native: true,
              }}
            >
              <option value="CPR">CPR (DKK) - 10 digits</option>
              <option value="SVNR">SVNR (GER) - 12 digits</option>
              <option value="Personnummer">Personnummer (SWE)</option>
              <option value="NIR">NIR (FRA) - 13 digits</option>
              <option value="NHS Number">NHS Number (UK)</option>
              <option value="SSN">SSN (USA)</option>
              <option value="Codice Fiscale">Codice Fiscale (ITA) - 11 digits</option>
              <option value="NUSS/NIE">NUSS/NIE (SPA) - min. 11 digits</option>
              <option value="Other">Other</option>
            </TextField>
            <TextField
              label="Social Security Number"
              fullWidth
              value={newPatientData.pii_number}
              onChange={(e) => setNewPatientData(prev => ({ ...prev, pii_number: e.target.value }))}
              variant="outlined"
              className={classes.orangeFocused}
              disabled={createPatientLoading}
            />
          </div>
        </Box>

        <Typography className={classes.sectionLabel}>Assessment</Typography>
        <Box className={classes.sectionPanel}>
          <Box className={classes.switchRow}>
            <Typography className={classes.switchLabel}>
              Send Assessment Link by Email?
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!newPatientData.send_assessment}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setNewPatientData(prev => ({
                      ...prev,
                      send_assessment: next,
                      send_full_assessment: next
                        ? (prev.send_full_assessment || (!prev.send_full_assessment && !prev.send_short_assessment))
                        : false,
                      send_short_assessment: next ? prev.send_short_assessment : false,
                    }));
                  }}
                  disabled={createPatientLoading}
                  classes={{ switchBase: classes.switchBase, track: classes.switchTrack }}
                />
              }
              label={newPatientData.send_assessment ? 'On' : 'Off'}
            />
          </Box>

          {newPatientData.send_assessment && (
            <Box className={classes.assessmentOptions}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!newPatientData.send_full_assessment}
                    onChange={(e) => {
                      setNewPatientData(prev => ({
                        ...prev,
                        send_full_assessment: e.target.checked,
                        send_short_assessment: e.target.checked ? false : prev.send_short_assessment,
                      }));
                    }}
                    disabled={createPatientLoading}
                    className={classes.checkbox}
                  />
                }
                label="Send full assessment link by email"
                classes={{ label: classes.checkboxLabel }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!newPatientData.send_short_assessment}
                    onChange={(e) => {
                      setNewPatientData(prev => ({
                        ...prev,
                        send_short_assessment: e.target.checked,
                        send_full_assessment: e.target.checked ? false : prev.send_full_assessment,
                      }));
                    }}
                    disabled={createPatientLoading}
                    className={classes.checkbox}
                  />
                }
                label="Send short-form assessment link by email"
                classes={{ label: classes.checkboxLabel }}
              />
            </Box>
          )}
        </Box>

        <Box className={classes.noteBox}>
          <Typography variant="body2" style={{ color: '#002D55' }}>
            <strong>Note:</strong> Treatment will start with an empty journal that can be updated when the patient completes an assessment or manually by the physio.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleCloseDialog}
          disabled={createPatientLoading}
          variant="outlined"
          className={classes.cancelButton}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreatePatient}
          variant="contained"
          disabled={createPatientLoading}
          className={classes.confirmButton}
        >
          {createPatientLoading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Create Patient'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePatientDialog;
